import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import brandingAPI from '../utils/brandingApi';
import { useCRM } from './CRMContext';

const BrandingContext = createContext();

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export const BrandingProvider = ({ children }) => {
  const { currentAgency } = useCRM();
  const [branding, setBranding] = useState({
    primaryColor: '#3B82F6',
    secondaryColor: '#10B981',
    accentColor: '#F59E0B',
    textColor: '#1F2937',
    backgroundColor: '#FFFFFF',
    userProfileImage: null,
    agencyProfileImage: null,
    agencyCoverImage: null,
    agencyName: '',
    agencyMotto: '',
    agencyDescription: '',
    agencyEmail: '',
    agencyPhone: '',
    agencyAddress: '',
    agencyWebsite: '',
    agencyLogo: null,
  });

  const [isLoading, setIsLoading] = useState(false);

  // Sync branding with current agency when it changes
  useEffect(() => {
    if (currentAgency) {
      const agencyId = String(currentAgency._id || currentAgency.id);
      console.log('🎨 Syncing branding with current agency:', {
        agencyId,
        name: currentAgency.name,
        motto: currentAgency.motto,
        logoUrl: currentAgency.logoUrl,
        theme: currentAgency.theme,
        fullAgency: currentAgency
      });
      
      // First, apply basic agency data from Agency model
      const themeColors = {};
      if (currentAgency.theme) {
        if (currentAgency.theme.primary) {
          themeColors.primaryColor = currentAgency.theme.primary;
        }
        if (currentAgency.theme.secondary) {
          themeColors.secondaryColor = currentAgency.theme.secondary;
        }
      }
      
      // Clear images immediately when agency changes to prevent showing old logo
      const basicBranding = {
        ...branding,
        // Agency identity from Agency model
        agencyName: currentAgency.name || '',
        agencyMotto: currentAgency.motto || '',
        // Theme colors from Agency model
        ...themeColors,
        // Clear images - they will be reloaded from Branding collection
        agencyLogo: null,
        agencyCoverImage: null,
        userProfileImage: null,
      };
      
      setBranding(basicBranding);
      
      // Then load agency-specific branding (images, etc.) from Branding collection
      loadAgencyBranding(agencyId);
    }
  }, [currentAgency?._id || currentAgency?.id]);

  // Load agency-specific branding from the Branding collection
  const loadAgencyBranding = async (agencyId) => {
    try {
      console.log('🎨 Loading agency-specific branding from API for agency:', agencyId);
      const response = await brandingAPI.getBranding(agencyId);
      if (response.data.success && response.data.data) {
        const agencyBranding = response.data.data;
        console.log('🎨 Agency branding loaded:', {
          agencyId: agencyBranding.agencyId,
          hasLogo: !!agencyBranding.agencyLogo,
          hasCover: !!agencyBranding.agencyCoverImage,
          hasProfile: !!agencyBranding.userProfileImage,
        });
        
        // Always set image fields from the API response (even if null)
        // This ensures we don't keep the previous agency's logo
        setBranding(prev => ({
          ...prev,
          // Always update image fields from branding collection (even if null)
          agencyLogo: agencyBranding.agencyLogo || null,
          agencyCoverImage: agencyBranding.agencyCoverImage || null,
          userProfileImage: agencyBranding.userProfileImage || null,
          // DO NOT update color fields - they come from Agency model only
          // primaryColor, secondaryColor, accentColor stay as-is (from Agency model)
        }));
      } else {
        // If API call fails or returns no data, explicitly clear images
        console.log('🎨 No branding data returned, clearing images');
        setBranding(prev => ({
          ...prev,
          agencyLogo: null,
          agencyCoverImage: null,
          userProfileImage: null,
        }));
      }
    } catch (error) {
      console.error('Failed to load agency branding:', error);
      // On error, clear images to prevent showing wrong logo
      setBranding(prev => ({
        ...prev,
        agencyLogo: null,
        agencyCoverImage: null,
        userProfileImage: null,
      }));
    }
  };

  // Load branding from database on initial mount (for users without agency)
  useEffect(() => {
    if (!currentAgency) {
      loadBrandingFromDatabase();
    }
  }, []);

  const loadBrandingFromDatabase = async () => {
    try {
      const response = await brandingAPI.getBranding();
      if (response.data.success) {
        const dbBranding = response.data.data;
        setBranding(prev => ({ ...prev, ...dbBranding }));
      }
    } catch (error) {
      console.error('Failed to load branding from database:', error);
      // Fallback to localStorage if database fails
      const savedBranding = localStorage.getItem('crm-branding');
      if (savedBranding) {
        try {
          const parsed = JSON.parse(savedBranding);
          setBranding(prev => ({ ...prev, ...parsed }));
        } catch (parseError) {
          console.error('Failed to parse saved branding:', parseError);
        }
      }
    }
  };

  // Save branding to localStorage whenever it changes (for offline support)
  useEffect(() => {
    localStorage.setItem('crm-branding', JSON.stringify(branding));
  }, [branding]);

  // Helper function to convert hex to HSL (defined outside useEffect for reuse)
  const hexToHsl = useCallback((hex) => {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Parse RGB
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    
    if (max === min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
        default: h = 0;
      }
    }
    
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  }, []);

  // Apply branding to CSS custom properties
  useEffect(() => {
    const root = document.documentElement;
    
    const updateForegroundColors = () => {
      const isDarkMode = root.classList.contains('dark');
      const primaryHsl = hexToHsl(branding.primaryColor);
      const secondaryHsl = hexToHsl(branding.secondaryColor);
      const primaryL = parseFloat(primaryHsl.split(' ')[2]);
      const secondaryL = parseFloat(secondaryHsl.split(' ')[2]);
      
      // For primary backgrounds (typically dark/colored), use light text in both modes for contrast
      // This ensures avatars and primary buttons are always readable
      root.style.setProperty('--primary-foreground', '210 40% 98%'); // Light text (works in both modes)
      root.style.setProperty('--secondary-foreground', secondaryL < 50 ? '210 40% 98%' : '222.2 84% 4.9%');
    };
    
    // Set brand-specific variables (hex format for direct use)
    root.style.setProperty('--brand-primary', branding.primaryColor);
    root.style.setProperty('--brand-secondary', branding.secondaryColor);
    root.style.setProperty('--brand-accent', branding.accentColor);
    root.style.setProperty('--brand-text', branding.textColor);
    root.style.setProperty('--brand-background', branding.backgroundColor);
    
    // Also update shadcn/ui primary and secondary (HSL format for theme system)
    // This ensures buttons and other components use branding colors
    const primaryHsl = hexToHsl(branding.primaryColor);
    const secondaryHsl = hexToHsl(branding.secondaryColor);
    
    root.style.setProperty('--primary', primaryHsl);
    root.style.setProperty('--secondary', secondaryHsl);
    
    // Update foreground colors
    updateForegroundColors();
    
    // Listen for dark mode changes
    const observer = new MutationObserver(() => {
      updateForegroundColors();
    });
    
    observer.observe(root, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, [branding, hexToHsl]);

  const updateBranding = useCallback((updates) => {
    setBranding(prev => ({ ...prev, ...updates }));
  }, []);

  const updateColor = useCallback(async (colorType, color) => {
    try {
      setIsLoading(true);
      
      // Update local state immediately for responsive UI
      setBranding(prev => ({
        ...prev,
        [colorType]: color
      }));

      // Save to database
      await brandingAPI.updateField(colorType, color);
    } catch (error) {
      console.error('Failed to update color:', error);
      // Revert local state on error
      setBranding(prev => ({
        ...prev,
        [colorType]: prev[colorType] // Revert to previous value
      }));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateImage = useCallback(async (imageType, image) => {
    try {
      setIsLoading(true);
      
      if (image instanceof File) {
        // Upload file to server
        const response = await brandingAPI.uploadImage(imageType, image);
        if (response.data.success) {
          setBranding(prev => ({
            ...prev,
            [imageType]: response.data.data.imageUrl
          }));
        }
      } else {
        // Direct URL update
        setBranding(prev => ({ ...prev, [imageType]: image }));
        await brandingAPI.updateField(imageType, image);
      }
    } catch (error) {
      console.error('Failed to update image:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resetBranding = useCallback(async () => {
    try {
      setIsLoading(true);
      console.log('🔍 Frontend: Starting branding reset');
      
      // Get current branding to preserve images
      const currentBranding = branding;
      
      // Define default colors and text (we know these)
      const defaultColorsAndText = {
        primaryColor: '#3B82F6',
        secondaryColor: '#10B981',
        accentColor: '#F59E0B',
        textColor: '#1F2937',
        backgroundColor: '#FFFFFF',
        agencyName: '',
        agencyMotto: '',
        agencyDescription: '',
        agencyEmail: '',
        agencyPhone: '',
        agencyAddress: '',
        agencyWebsite: '',
      };
      
      console.log('🔍 Frontend: Preserving images during reset:', {
        userProfileImage: currentBranding?.userProfileImage ? 'preserved' : 'none',
        agencyLogo: currentBranding?.agencyLogo ? 'preserved' : 'none',
        agencyProfileImage: currentBranding?.agencyProfileImage ? 'preserved' : 'none',
        agencyCoverImage: currentBranding?.agencyCoverImage ? 'preserved' : 'none'
      });
      
      // Update only colors and text fields, preserve images to prevent flickering
      setBranding(prev => ({
        ...prev, // Keep existing images and other fields
        ...defaultColorsAndText // Override only colors and text
      }));
      
      // Reset in database
      console.log('🔍 Frontend: Calling brandingAPI.resetBranding()');
      await brandingAPI.resetBranding();
      console.log('🔍 Frontend: Reset completed successfully');
      
    } catch (error) {
      console.error('Failed to reset branding:', error);
    } finally {
      setIsLoading(false);
    }
  }, [branding]);

  const saveBranding = useCallback(async (brandingData) => {
    try {
      setIsLoading(true);
      
      // Update local state
      setBranding(prev => ({ ...prev, ...brandingData }));
      
      // Save to database
      await brandingAPI.updateBranding(brandingData);
      
      return { success: true };
    } catch (error) {
      console.error('Failed to save branding:', error);
      return { success: false, error: error.message };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const deleteImage = useCallback(async (imageType) => {
    try {
      setIsLoading(true);
      
      // Update local state
      setBranding(prev => ({ ...prev, [imageType]: null }));
      
      // Delete from database
      await brandingAPI.deleteImage(imageType);
    } catch (error) {
      console.error('Failed to delete image:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getBrandingCSS = useCallback(() => {
    return {
      '--brand-primary': branding.primaryColor,
      '--brand-secondary': branding.secondaryColor,
      '--brand-accent': branding.accentColor,
      '--brand-text': branding.textColor,
      '--brand-background': branding.backgroundColor,
    };
  }, [branding]);

  const getBrandingClasses = useCallback(() => {
    return {
      primary: `text-[${branding.primaryColor}]`,
      primaryBg: `bg-[${branding.primaryColor}]`,
      primaryBorder: `border-[${branding.primaryColor}]`,
      secondary: `text-[${branding.secondaryColor}]`,
      secondaryBg: `bg-[${branding.secondaryColor}]`,
      secondaryBorder: `border-[${branding.secondaryColor}]`,
      accent: `text-[${branding.accentColor}]`,
      accentBg: `bg-[${branding.accentColor}]`,
      accentBorder: `border-[${branding.accentColor}]`,
    };
  }, [branding]);

  const value = {
    branding,
    isLoading,
    updateBranding,
    updateColor,
    updateImage,
    deleteImage,
    resetBranding,
    saveBranding,
    getBrandingCSS,
    getBrandingClasses,
    loadBrandingFromDatabase,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};
